/**
 * Order-list alerts, with thanks (owner, 2026-10-01: "Managers should get notified: new item (sanitizer) added to order
 * list. Thank Susan for letting you know you're running low."). A pure core: the API only gathers, stores and sends.
 *
 * | Rule | Why |
 * |---|---|
 * | O1 When anyone says a supply is "Out" or "Running low" (the step's tap, the product page, a stock check), or a count drops it below its par, it is on the order list (ordering.ts), and the managers are told. A person's own word always counts; a count counts only when it newly crosses the line | A person who says "I'm out" has told us something even if a count already said it |
 * | O2 The alert goes to managers, admins and owners at that place, never to staff, and never to the person who flagged it (they know) | The order list is the manager's job (ordering.ts: manager records orders) |
 * | O3 Words: "Sanitizer added to the order list. Susan said you're running low." The person who flagged it is named, so the manager can thank them. No health information (T4): a supply name with a health word reads "A supply" | Credit goes to the person who spoke up (G2b). The same scan as push.ts |
 * | O4 Batching: adds at one place within BATCH_MINUTES of the first add are one alert ("3 items added to the order list."), naming who added each. The alert is one in-app row per manager that is updated in place, and one push for the batch | Three taps in a closing rush are one interruption, not three |
 * | O5 No duplicates: the same supply flagged again on the same local day is not a new alert. The second person is credited "also noticed" (never a second push) | A rush makes three people say the same thing; once is enough for the manager, and each person is still seen |
 * | O6 "Thank Susan": one tap sends a kudos to the person who flagged it, with a preset line: "Thanks for catching that we were low on sanitizer." Once per person per flag: a second tap is "Already thanked", not a second kudos | The thanks is earned by the act, and sent once |
 * | O7 The person who flagged it is told, in one line: "Thanks, the manager knows." If nobody else here can get the alert (the flagger is the only manager), the line is "Thanks. It's on the order list." Never a claim that someone was told when nobody was | Truth before comfort (G2b) |
 * | O8 Taking it back (Undo within the same minute) withdraws the flag, and the alert loses that line (or goes, if it was the only one) | A mis-tap should not leave a manager chasing nothing |
 */
import type { Supply } from "./actions.ts";
import { needState } from "./stepsupplies.ts";
import { hasHealthWords } from "./push.ts";
import { firstName } from "./actions.ts";
import type { ShiftNotice } from "./handoff.ts";
import type { Id, IsoDate, IsoDateTime } from "./model.ts";

export type FlagLevel = "low" | "out";

/** Minutes after a batch's first add during which further adds join it. "About 10 minutes" (the owner's words). */
export const BATCH_MINUTES = 10;

export interface FlagPerson { employeeId?: Id; userId: Id; name: string }

/** One supply flagged at one place on one local day (migration 0062 order_flags). */
export interface OrderFlag {
  id: Id;
  orgId: Id;
  locationId: Id;
  supplyKey: string;
  name: string;
  level: FlagLevel;
  /** The place's local day: with the supply, the dedup key (O5). */
  date: IsoDate;
  at: IsoDateTime;
  by: FlagPerson;
  /** Others who said the same later that day (O5). */
  also: (FlagPerson & { at: IsoDateTime })[];
  /** The first flag's id in this alert (O4). */
  batchId: Id;
  /** Who has been thanked, and by whom (O6). */
  thanked: { employeeId: Id; at: IsoDateTime; by: Id }[];
  withdrawn?: true;
}

/* ─────────────── what counts (O1) ─────────────── */

/**
 * O1: did this change put the supply on the order list? `said` is the person's own word in this request (the "stock" field):
 * a word of "low" or "out" counts every time. Otherwise a count counts only when it newly crosses the line.
 */
export function flagLevelOf(before: Supply | undefined, after: Supply, said?: "have" | "low" | "out" | null): FlagLevel | undefined {
  if (said === "low" || said === "out") return said;
  const was = needState(before), now = needState(after);
  if ((now === "low" || now === "out") && was !== now && was !== "low" && was !== "out") return now;
  return undefined;
}

/* ─────────────── adding a flag (O4, O5) ─────────────── */

export type FlagOutcome =
  /** First time today: a new alert, or it joined an open batch (`joined`). */
  | { kind: "new"; flag: OrderFlag; joined: boolean }
  /** Someone else already flagged it today: this person is credited "also noticed" (O5). */
  | { kind: "also"; flag: OrderFlag }
  /** The same person said it again: nothing changes. */
  | { kind: "same"; flag: OrderFlag };

export interface FlagInput {
  orgId: Id; locationId: Id; supplyKey: string; name: string; level: FlagLevel; date: IsoDate; at: IsoDateTime; by: FlagPerson; id: Id;
}

const samePerson = (a: FlagPerson, b: FlagPerson) => a.userId === b.userId || (!!a.employeeId && a.employeeId === b.employeeId);

/** O4, O5: the flag this request makes, given every flag at the place so far. Pure; the caller stores what comes back. */
export function addFlag(flags: readonly OrderFlag[], i: FlagInput): FlagOutcome {
  const live = flags.filter((f) => !f.withdrawn && f.locationId === i.locationId);
  const dup = live.find((f) => f.supplyKey === i.supplyKey && f.date === i.date);
  if (dup) {
    if (samePerson(dup.by, i.by) || dup.also.some((p) => samePerson(p, i.by))) return { kind: "same", flag: dup };
    // A person's own word may also raise the level (low, then out): the stored level follows, the alert does not repeat.
    const level: FlagLevel = dup.level === "out" || i.level === "out" ? "out" : "low";
    return { kind: "also", flag: { ...dup, level, also: [...dup.also, { ...i.by, at: i.at }] } };
  }
  // O4: the open batch is the newest one whose first add was within BATCH_MINUTES.
  const starts = new Map<Id, IsoDateTime>();
  for (const f of live) if (f.id === f.batchId) starts.set(f.batchId, f.at);
  const open = [...starts].filter(([, at]) => Date.parse(i.at) - Date.parse(at) <= BATCH_MINUTES * 60_000 && i.at >= at).sort((a, b) => b[1].localeCompare(a[1]))[0];
  const flag: OrderFlag = { id: i.id, orgId: i.orgId, locationId: i.locationId, supplyKey: i.supplyKey, name: i.name, level: i.level, date: i.date, at: i.at, by: i.by, also: [], batchId: open ? open[0] : i.id, thanked: [] };
  return { kind: "new", flag, joined: !!open };
}

/** The flags in one alert, oldest first. */
export const batchFlags = (flags: readonly OrderFlag[], batchId: Id): OrderFlag[] => flags.filter((f) => f.batchId === batchId && !f.withdrawn).sort((a, b) => a.at.localeCompare(b.at) || a.id.localeCompare(b.id));

/* ─────────────── words (O3, O7) ─────────────── */

/** O3: a supply's name as an alert may say it. A health word: "A supply". */
export function safeSupplyName(name: string): string {
  const t = name.replace(/\s+/g, " ").trim();
  return !t || hasHealthWords(t) ? "A supply" : t.length > 60 ? `${t.slice(0, 59).trimEnd()}…` : t;
}

const lowerFirst = (s: string) => (/^[A-Z][a-z]/.test(s) ? s.charAt(0).toLowerCase() + s.slice(1) : s);
const said = (l: FlagLevel) => (l === "out" ? "you're out" : "you're running low");

/** "Raj also noticed." / "Raj and Jo also noticed." */
export function alsoLine(f: Pick<OrderFlag, "also">): string {
  const names = f.also.map((p) => firstName(p.name));
  if (!names.length) return "";
  const who = names.length === 1 ? names[0]! : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
  return `${who} also noticed.`;
}

export interface OrderAlertText { title: string; body: string }

/**
 * O3, O4: the alert's words. One item: "Sanitizer added to the order list." and "Susan said you're running low." Several:
 * "3 items added to the order list." and who added each: "Sanitizer (Susan), Gloves (Raj)."
 */
export function orderAlertText(flags: readonly OrderFlag[]): OrderAlertText {
  const live = flags.filter((f) => !f.withdrawn);
  if (live.length === 0) return { title: "Nothing added to the order list.", body: "" };
  if (live.length === 1) {
    const f = live[0]!;
    const also = alsoLine(f);
    return { title: `${safeSupplyName(f.name)} added to the order list.`, body: `${firstName(f.by.name)} said ${said(f.level)}.${also ? ` ${also}` : ""}` };
  }
  const parts = live.map((f) => `${safeSupplyName(f.name)} (${firstName(f.by.name)}${f.also.length ? ` and ${f.also.map((p) => firstName(p.name)).join(", ")}` : ""})`);
  return { title: `${live.length} items added to the order list.`, body: `${parts.join(", ")}.` };
}

/** O6: the kudos line's note. "Thanks for catching that we were low on sanitizer." */
export function thanksNote(f: Pick<OrderFlag, "name" | "level">): string {
  const n = safeSupplyName(f.name);
  const what = n === "A supply" ? "supplies" : lowerFirst(n);
  return f.level === "out" ? `Thanks for catching that we ran out of ${what}.` : `Thanks for catching that we were low on ${what}.`;
}

/** O7: what the person who flagged it reads. `told` is how many managers could get the alert. */
export function flaggerLine(told: number, o: { also?: boolean } = {}): string {
  if (told > 0) return o.also ? "Thanks. The manager already knows, and now knows you noticed too." : "Thanks, the manager knows.";
  return "Thanks. It's on the order list.";
}

/** The button words, shared by web and phone. */
export const ORDER_ALERT_WORDS = {
  thank: (name: string) => `Thank ${firstName(name)}`,
  thanked: (name: string) => `Thanked ${firstName(name)}`,
  open: "Open the order list",
  kind: "Order list",
  alreadyThanked: "Already thanked.",
} as const;

/* ─────────────── what the manager's card reads ─────────────── */

export interface OrderAlertItemView {
  flagId: Id;
  key: string;
  name: string;
  level: FlagLevel;
  by: { employeeId?: Id; name: string };
  also: { employeeId?: Id; name: string }[];
  /** First names already thanked. */
  thanked: Id[];
}

/** GET /today me.handoffs[].order: the lines of one alert, with who can be thanked. */
export interface OrderAlertView { items: OrderAlertItemView[] }

export function orderAlertView(flags: readonly OrderFlag[]): OrderAlertView {
  return {
    items: flags.filter((f) => !f.withdrawn).map((f) => ({
      flagId: f.id, key: f.supplyKey, name: safeSupplyName(f.name), level: f.level,
      by: { ...(f.by.employeeId ? { employeeId: f.by.employeeId } : {}), name: firstName(f.by.name) },
      also: f.also.map((p) => ({ ...(p.employeeId ? { employeeId: p.employeeId } : {}), name: firstName(p.name) })),
      thanked: f.thanked.map((t) => t.employeeId),
    })),
  };
}


/* ─────────────── one flag, planned (the API and both demos run the same plan) ─────────────── */

export interface AlertRows { put: ShiftNotice[]; remove: Id[] }

export interface AlertRowsInput {
  /** Every flag at the place (the batch's are picked out). */
  flags: readonly OrderFlag[];
  batchId: Id;
  /** Managers, admins and owners at the place with a profile. */
  managers: readonly { id: Id }[];
  /** The "order" notice rows at the place (the batch's are picked out). */
  rows: readonly ShiftNotice[];
  orgId: Id; locationId: Id; at: IsoDateTime; newId: () => Id; fromEmployeeId?: Id;
}

/**
 * O2, O4: keeps each manager's ONE row for this alert in step with the flags in it. A manager who flagged every line is
 * not told (they know); a batch with no flag left loses its rows. A row already sent keeps its `pushedAt`: the push went once.
 */
export function planAlertRows(i: AlertRowsInput): AlertRows {
  const mine = batchFlags(i.flags, i.batchId);
  const rows = i.rows.filter((r) => r.kind === "order" && r.refId === i.batchId && r.locationId === i.locationId);
  if (!mine.length) return { put: [], remove: rows.map((r) => r.id) };
  const w = orderAlertText(mine);
  const put: ShiftNotice[] = [];
  for (const m of i.managers) {
    if (mine.every((f) => f.by.employeeId === m.id)) continue;
    const row = rows.find((r) => r.toEmployeeId === m.id);
    if (row) { if (row.title !== w.title || row.body !== w.body) put.push({ ...row, title: w.title, body: w.body }); continue; }
    put.push({ id: i.newId(), orgId: i.orgId, locationId: i.locationId, toEmployeeId: m.id, kind: "order", refId: i.batchId, title: w.title, body: w.body, createdAt: i.at, ...(i.fromEmployeeId ? { fromEmployeeId: i.fromEmployeeId } : {}) });
  }
  return { put, remove: [] };
}

export interface FlagPlanInput {
  flags: readonly OrderFlag[];
  supply: Pick<Supply, "key" | "name">;
  level: FlagLevel;
  by: FlagPerson;
  /** The managers at the place (names: for the flagger's line "Maya gets the alert", landed.ts L6). */
  managers: readonly { id: Id; name?: string }[];
  rows: readonly ShiftNotice[];
  orgId: Id; locationId: Id; date: IsoDate; at: IsoDateTime; newId: () => Id;
}

export interface FlagPlan {
  outcome: FlagOutcome["kind"];
  flag: OrderFlag;
  /** The flag to store (absent when nothing changed: the same person said it again). */
  put?: OrderFlag;
  rows: AlertRows;
  /** O7: the one line for the person who said it. */
  told: string;
  /** How many managers could be told. */
  managers: number;
  /** Their full names (never the flagger's), for landed.ts orderToldLine. */
  toldNames: string[];
}

/**
 * The time to write on a new flag: now, but never at or before the newest flag at the place. Flags are listed by time, and two
 * said in the same millisecond (a pinned test clock) must keep the order they were said in; ids are random and cannot settle it.
 */
export function flagAt(flags: readonly Pick<OrderFlag, "locationId" | "at">[], locationId: Id, now: IsoDateTime): IsoDateTime {
  const last = flags.filter((f) => f.locationId === locationId).map((f) => f.at).sort().at(-1);
  return last && last >= now ? new Date(Date.parse(last) + 1).toISOString() : now;
}

/** O1-O5, O7: somebody said a supply is out or low. */
export function planFlag(i: FlagPlanInput): FlagPlan {
  const at = flagAt(i.flags, i.locationId, i.at);
  const out = addFlag(i.flags, { id: i.newId(), orgId: i.orgId, locationId: i.locationId, supplyKey: i.supply.key, name: i.supply.name, level: i.level, date: i.date, at, by: i.by });
  const managers = i.managers.filter((m) => m.id !== i.by.employeeId);
  const toldNames = managers.map((m) => m.name ?? "").filter(Boolean);
  if (out.kind === "same") return { outcome: "same", flag: out.flag, rows: { put: [], remove: [] }, told: flaggerLine(managers.length), managers: managers.length, toldNames };
  const all = [...i.flags.filter((f) => f.id !== out.flag.id), out.flag];
  const rows = planAlertRows({ flags: all, batchId: out.flag.batchId, managers, rows: i.rows, orgId: i.orgId, locationId: i.locationId, at, newId: i.newId, ...(i.by.employeeId ? { fromEmployeeId: i.by.employeeId } : {}) });
  return { outcome: out.kind, flag: out.flag, put: out.flag, rows, told: flaggerLine(managers.length, { also: out.kind === "also" }), managers: managers.length, toldNames };
}

/** Undo within a couple of minutes takes the caller's own flag back (O8). */
export const UNDO_FLAG_MIN = 2;

export interface WithdrawPlanInput {
  flags: readonly OrderFlag[];
  supplyKey: string;
  userId: Id;
  managers: readonly { id: Id }[];
  rows: readonly ShiftNotice[];
  orgId: Id; locationId: Id; date: IsoDate; now: IsoDateTime; newId: () => Id;
}

/** O8: Undo. Their own flag goes (a second person who also noticed carries it); or their "also noticed" goes. */
export function planWithdraw(i: WithdrawPlanInput): { changed: false } | { changed: true; put: OrderFlag; rows: AlertRows } {
  const f = i.flags.find((x) => x.supplyKey === i.supplyKey && !x.withdrawn && x.date === i.date && x.locationId === i.locationId);
  if (!f) return { changed: false };
  const recent = (iso: IsoDateTime) => Date.parse(i.now) - Date.parse(iso) <= UNDO_FLAG_MIN * 60_000;
  const mine = (p: { userId: Id }) => p.userId === i.userId;
  let next: OrderFlag | undefined;
  if (mine(f.by) && recent(f.at) && f.thanked.length === 0) {
    const [first, ...rest] = f.also;
    next = first ? { ...f, by: { userId: first.userId, name: first.name, ...(first.employeeId ? { employeeId: first.employeeId } : {}) }, also: rest } : { ...f, withdrawn: true };
  } else if (f.also.some((p) => mine(p) && recent(p.at))) next = { ...f, also: f.also.filter((p) => !mine(p)) };
  if (!next) return { changed: false };
  const all = i.flags.map((x) => (x.id === next!.id ? next! : x));
  return { changed: true, put: next, rows: planAlertRows({ flags: all, batchId: f.batchId, managers: i.managers, rows: i.rows, orgId: i.orgId, locationId: i.locationId, at: i.now, newId: i.newId }) };
}

export type ThankPlan =
  | { ok: true; already: true; flag: OrderAlertItemView }
  | { ok: true; already: false; target: FlagPerson & { employeeId: Id }; text: string; next: OrderFlag }
  | { ok: false; status: number; error: string };

/** O6: who a "Thank Susan" tap thanks, with what words, once. */
export function planThank(f: OrderFlag, want: Id | undefined, byUserId: Id, at: IsoDateTime): ThankPlan {
  if (f.withdrawn) return { ok: false, status: 404, error: "That alert wasn't found." };
  const credited = [f.by, ...f.also].filter((p): p is FlagPerson & { employeeId: Id } => !!p.employeeId);
  const target = want ? credited.find((p) => p.employeeId === want) : credited[0];
  if (!target) return { ok: false, status: 400, error: want ? "That person didn't flag this item." : "Nobody who flagged this is on the team list, so there's nobody to thank here." };
  if (f.thanked.some((t) => t.employeeId === target.employeeId)) return { ok: true, already: true, flag: orderAlertView([f]).items[0]! };
  return { ok: true, already: false, target, text: thanksNote(f), next: { ...f, thanked: [...f.thanked, { employeeId: target.employeeId, at, by: byUserId }] } };
}

/* ─────────────── what the routes answer ─────────────── */

/** POST /orgs/:orgId/order-alerts/:flagId/thank. */
export interface ThankBody { employeeId?: Id }
/** `already`: this person was thanked before; no second kudos was written. `line`: "Thanked Susan." / "Already thanked." */
export type ThankResponse =
  | { already: true; line: string; flag: OrderAlertItemView }
  | { already: false; line: string; kudos: { id: Id; at: IsoDateTime; toEmployeeId: Id; line: string }; flag: OrderAlertItemView };
